/**
 * Classic + granular sampler voice helpers — used by the TS kernel.
 */

export type SamplerVoiceGeometry = {
  position: number;
  rate: number;
  sampleStart: number;
  sampleEnd: number;
  loopEnabled: boolean;
  loopStart: number;
  loopEnd: number;
  /** Equal-power crossfade length at the loop splice (source frames). */
  loopXfade?: number;
  reverse: boolean;
};

export type GrainWindow = "hann" | "tri" | "rect";

export type Grain = {
  active: boolean;
  /** Read position in source frames. */
  pos: number;
  /** Playback rate for this grain. */
  rate: number;
  age: number;
  duration: number;
};

export type GranularState = {
  /** Playback head (frames) — advances by stretch, independent of grain pitch. */
  head: number;
  spawnAcc: number;
  grains: Grain[];
};

export function createGranularState(maxGrains = 8): GranularState {
  return {
    head: 0,
    spawnAcc: 0,
    grains: Array.from({ length: maxGrains }, () => ({
      active: false,
      pos: 0,
      rate: 1,
      age: 0,
      duration: 1,
    })),
  };
}

/** Linear interpolate a frame from mono PCM. Position may be outside [start,end). */
export function sampleAt(pcm: Float32Array, position: number, sampleEnd: number): number {
  if (pcm.length === 0) {
    return 0;
  }
  const maxIndex = Math.max(0, Math.min(sampleEnd, pcm.length) - 1);
  if (position <= 0) {
    return pcm[0] ?? 0;
  }
  if (position >= maxIndex) {
    return pcm[maxIndex] ?? 0;
  }
  const i0 = Math.floor(position);
  const i1 = Math.min(i0 + 1, maxIndex);
  const frac = position - i0;
  const s0 = pcm[i0] ?? 0;
  const s1 = pcm[i1] ?? s0;
  return s0 + (s1 - s0) * frac;
}

/**
 * Advance read head. Returns sample value; sets `done` when one-shot finished.
 *
 * Sustain-loop (SF2-style): play from sampleStart through the loop, wrap only
 * after crossing loopEnd, and on note-off (loopEnabled=false) continue past
 * loopEnd to sampleEnd for the release tail.
 *
 * Loop wraps use equal-power crossfade. After wrapping we skip the overlapped
 * head (`+xfade`) so playback continues at the fade destination — a hard wrap
 * back to raw `loopStart` would click even when both edges are zero crossings
 * (slope / material mismatch).
 */
export function advanceClassicSample(
  pcm: Float32Array,
  voice: SamplerVoiceGeometry,
): { sample: number; done: boolean } {
  if (!pcm.length || voice.sampleEnd <= voice.sampleStart) {
    return { sample: 0, done: true };
  }

  let sample = sampleAt(pcm, voice.position, voice.sampleEnd);

  if (voice.loopEnabled && voice.loopEnd > voice.loopStart + 1) {
    const loopLen = voice.loopEnd - voice.loopStart;
    const xfade = Math.max(
      0,
      Math.min(Math.floor(voice.loopXfade ?? 0), Math.floor(loopLen / 2) - 1),
    );
    if (xfade > 0) {
      sample = applyLoopXfade(pcm, voice, sample, xfade);
    }

    const step = voice.reverse ? -voice.rate : voice.rate;
    voice.position += step;

    if (!voice.reverse) {
      let wrapped = false;
      while (voice.position >= voice.loopEnd) {
        voice.position -= loopLen;
        wrapped = true;
      }
      if (wrapped && xfade > 0) {
        // Resume at the material the fade mixed toward (loopStart + xfade).
        voice.position += xfade;
        const bodyLen = Math.max(1, loopLen - xfade);
        while (voice.position >= voice.loopEnd) {
          voice.position -= bodyLen;
        }
      }
      // Sustain-loop: allow pre-loop attack (position < loopStart). Only clamp
      // after a wrap so float error can't leave us below the loop body.
      if (wrapped && voice.position < voice.loopStart) {
        voice.position = voice.loopStart;
      }
    } else {
      let wrapped = false;
      while (voice.position < voice.loopStart) {
        voice.position += loopLen;
        wrapped = true;
      }
      if (wrapped && xfade > 0) {
        voice.position -= xfade;
        const bodyLen = Math.max(1, loopLen - xfade);
        while (voice.position < voice.loopStart) {
          voice.position += bodyLen;
        }
      }
      // Reverse attack: material after loopEnd plays first; don't snap into the loop.
      if (wrapped && voice.position >= voice.loopEnd) {
        voice.position = voice.loopEnd - 1e-6;
      }
    }
    return { sample, done: false };
  }

  const step = voice.reverse ? -voice.rate : voice.rate;
  voice.position += step;

  if (!voice.reverse && voice.position >= voice.sampleEnd) {
    return { sample, done: true };
  }
  if (voice.reverse && voice.position < voice.sampleStart) {
    return { sample, done: true };
  }
  return { sample, done: false };
}

function applyLoopXfade(
  pcm: Float32Array,
  voice: SamplerVoiceGeometry,
  primary: number,
  xfade: number,
): number {
  if (!voice.reverse) {
    const fadeStart = voice.loopEnd - xfade;
    if (voice.position < fadeStart || voice.position >= voice.loopEnd) {
      return primary;
    }
    const t = (voice.position - fadeStart) / xfade;
    const inPos = voice.loopStart + (voice.position - fadeStart);
    const secondary = sampleAt(pcm, inPos, voice.sampleEnd);
    const outG = Math.cos(t * Math.PI * 0.5);
    const inG = Math.sin(t * Math.PI * 0.5);
    return primary * outG + secondary * inG;
  }

  const fadeEnd = voice.loopStart + xfade;
  if (voice.position > fadeEnd || voice.position <= voice.loopStart) {
    return primary;
  }
  const t = (fadeEnd - voice.position) / xfade;
  const inPos = voice.loopEnd - (fadeEnd - voice.position);
  const secondary = sampleAt(pcm, inPos, voice.sampleEnd);
  const outG = Math.cos(t * Math.PI * 0.5);
  const inG = Math.sin(t * Math.PI * 0.5);
  return primary * outG + secondary * inG;
}

/** Default classic-loop crossfade (~20 ms), capped to a quarter of the loop. */
export function defaultLoopXfade(loopLengthFrames: number, sampleRate: number): number {
  const len = Math.max(2, Math.round(loopLengthFrames));
  const twentyMs = Math.max(16, Math.round(sampleRate * 0.02));
  return Math.max(0, Math.min(twentyMs, Math.floor(len / 4)));
}

/** Resolve preset xfade or fall back to a soft equal-power default (~20 ms). */
export function resolveLoopXfade(
  presetXfade: number | undefined,
  loopLengthFrames: number,
  sampleRate: number,
): number {
  const len = Math.max(2, Math.round(loopLengthFrames));
  const max = Math.max(0, Math.floor(len / 2) - 1);
  const fallback = defaultLoopXfade(len, sampleRate);
  if (presetXfade != null && Number.isFinite(presetXfade) && presetXfade > 0) {
    // Keep at least the default — earlier 5 ms values still clicked on slope mismatch.
    return Math.min(Math.max(Math.round(presetXfade), fallback), max);
  }
  return Math.min(fallback, max);
}

export function grainWindowGain(window: GrainWindow, t: number): number {
  const x = Math.max(0, Math.min(1, t));
  switch (window) {
    case "rect":
      return 1;
    case "tri":
      return 1 - Math.abs(2 * x - 1);
    case "hann":
    default:
      return 0.5 * (1 - Math.cos(2 * Math.PI * x));
  }
}

/**
 * One output sample of simple granular playback.
 *
 * - `head` advances at `stretch * headRate` (time stretch without pitch).
 * - Each grain reads at `pitchRate` (pitch without stretch).
 * - `density` is grains per second; size is in seconds.
 * - `wrap` (default true): loop the head/grains in the region. When false,
 *   head stops at `sampleEnd` and `done` fires after grains drain (one-shots).
 */
export function advanceGranularSample(
  pcm: Float32Array,
  state: GranularState,
  opts: {
    sampleStart: number;
    sampleEnd: number;
    /** Frames of head advance per output sample at stretch=1. */
    headRate: number;
    stretch: number;
    /** Grain read rate (frames per output sample). */
    pitchRate: number;
    grainSizeSec: number;
    density: number;
    spray: number;
    window: GrainWindow;
    dt: number;
    /** When false, do not wrap — used by classic preserveDuration one-shots. */
    wrap?: boolean;
  },
): { sample: number; done: boolean } {
  const start = opts.sampleStart;
  const end = Math.max(start + 1, opts.sampleEnd);
  const span = end - start;
  const wrap = opts.wrap !== false;

  const stretch = Math.max(0.01, Math.min(8, opts.stretch));
  state.head += opts.headRate * stretch;
  let headPastEnd = false;
  if (state.head >= end) {
    if (wrap) {
      state.head = start + ((state.head - start) % span);
    } else {
      state.head = end;
      headPastEnd = true;
    }
  }
  if (state.head < start) {
    state.head = start;
  }

  const density = Math.max(0.1, Math.min(64, opts.density));
  if (!headPastEnd) {
    state.spawnAcc += density * opts.dt;
    while (state.spawnAcc >= 1) {
      state.spawnAcc -= 1;
      const grain = state.grains.find(g => !g.active) ?? state.grains[0]!;
      const sprayFrames = opts.spray * span * (Math.random() * 2 - 1);
      grain.active = true;
      grain.pos = Math.max(start, Math.min(end - 1, state.head + sprayFrames));
      grain.rate = opts.pitchRate;
      grain.age = 0;
      grain.duration = Math.max(0.001, Math.min(0.5, opts.grainSizeSec));
    }
  }

  let mix = 0;
  let active = 0;
  for (const grain of state.grains) {
    if (!grain.active) {
      continue;
    }
    active += 1;
    const t = grain.age / grain.duration;
    if (t >= 1) {
      grain.active = false;
      continue;
    }
    const g = grainWindowGain(opts.window, t);
    mix += sampleAt(pcm, grain.pos, end) * g;
    grain.pos += grain.rate;
    if (grain.pos >= end) {
      if (wrap) {
        grain.pos = start + ((grain.pos - start) % span);
      } else {
        grain.active = false;
        continue;
      }
    }
    if (grain.pos < start) {
      grain.pos = start;
    }
    grain.age += opts.dt;
  }

  const done = wrap
    ? active === 0 && stretch <= 0
    : headPastEnd && active === 0;
  return { sample: mix, done };
}

/** Root-key relative playback rate (frames per output sample). */
export function classicRateFromRoot(
  sampleRate: number,
  engineSampleRate: number,
  note: number,
  rootKey: number,
  tuneCents = 0,
): number {
  const pitchRatio = 2 ** ((note - rootKey) / 12 + tuneCents / 1200);
  return (sampleRate / Math.max(1, engineSampleRate)) * pitchRatio;
}

/** One-cycle tables: one period of the wave = one period of the note. */
export function classicRateFromCycle(
  cycleLength: number,
  engineSampleRate: number,
  note: number,
  tuneCents = 0,
): number {
  const hz = 440 * 2 ** ((note - 69) / 12 + tuneCents / 1200);
  return (cycleLength * hz) / Math.max(1, engineSampleRate);
}

export function dbToGain(db: number): number {
  return 10 ** (db / 20);
}
